import { useEffect, useRef, useState } from "react";
import { type CustomCellEditorProps } from "ag-grid-react";

type EditorType = "numeric" | "text";

interface Props extends CustomCellEditorProps {
  maxLength?: number;
  type?: EditorType;
}

const AggridInputCellEditor = (props: Props) => {
  const type: EditorType = props.type ?? "numeric";
  const maxLength = props.maxLength ?? (type === "numeric" ? 7 : 100);

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>(
    props.value !== null && props.value !== undefined ? String(props.value) : ""
  );

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  const updateValue = (newStr: string) => {
    setValue(newStr);
    if (newStr === "") {
      props.onValueChange(null);
    } else if (type === "numeric") {
      props.onValueChange(Number(newStr));
    } else {
      props.onValueChange(newStr);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      props.stopEditing(true);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      props.stopEditing();
      return;
    }

    if (type === "numeric") {
      const allowed = [
        "Backspace", "Delete",
        "ArrowLeft", "ArrowRight", "Home", "End",
      ];
      if (allowed.includes(e.key)) return;
      if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key.toLowerCase())) return;

      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let cleaned = e.target.value;
    if (type === "numeric") {
      cleaned = cleaned.replace(/[^0-9]/g, "");
    }
    if (cleaned.length <= maxLength) {
      updateValue(cleaned);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode={type === "numeric" ? "numeric" : "text"}
      maxLength={maxLength}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        outline: "none",
        backgroundColor: "transparent",
        textAlign: type === "numeric" ? "center" : "center",
        padding: "0 4px",
        boxSizing: "border-box",
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        color: "inherit",
      }}
    />
  );
};

export default AggridInputCellEditor;